"use client";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import { FaPlay } from "react-icons/fa";
import { HiMiniPause } from "react-icons/hi2";
import {useRouter} from "next/navigation";

const CustomSwiper = ({
                        slides = [],
                        effect = "slide",
                        progressBar = false,
                        pause = false,
                        navigation = false,
                        delay = 3000,
                        className = "",
                        loop = false,
                        speed,
                        autoplay,
                        autoplayDelay,
                        direction = "horizontal",
                        slidesPerView = "auto",
                        slidesPerGroup = 1,
                        spaceBetween = 0,
                        slideOnclick,
                        currentSlideIdx = undefined,
                        slideButton = false,
                        nested = false,
                        touchReleaseOnEdges = false,
                      }) => {
    const swiperRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const [progressList, setProgressList] = useState(
        Array(slides.length).fill(0)
    );
    const [activeIndex, setActiveIndex] = useState(0);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(slides.length > 1);
    const router = useRouter();
    useEffect(() => {
        setProgressList(Array(slides.length).fill(0));
        setActiveIndex(0);
    }, [slides.length]);

    // 프로그래스바 애니메이션
    useEffect(() => {
        let timer;
        if (isPlaying && progressBar && slides.length > 0) {
            timer = setInterval(() => {
                setProgressList((prev) =>
                    prev.map((val, idx) => {
                        if (idx === activeIndex) {
                            if (val >= 100) return 100;
                            return val + 100 / (delay / 50); // 50ms tick 기준
                        }
                        return val;
                    })
                );
            }, 50);
        }
        return () => clearInterval(timer);
    }, [isPlaying, progressBar, delay, activeIndex, slides.length]);

    const handleSlideChange = (swiper) => {
        const realIndex = swiper.realIndex;
        const n = slides.length;
        if (n === 0) return;
        const nextIdx = (activeIndex + 1) % n;
        const prevIdx = (activeIndex - 1 + n) % n;
        const isForward =
            realIndex === nextIdx || (activeIndex === n - 1 && realIndex === 0);
        const isBackward =
            realIndex === prevIdx || (activeIndex === 0 && realIndex === n - 1);

        setProgressList((prev) => {
            let updated = [...prev];
            if (activeIndex === n - 1 && realIndex === 0 && isForward) {
                updated = Array(n).fill(0);
            } else if (isForward) {
                updated[activeIndex] = 100;
            } else if (isBackward) {
                updated[activeIndex] = 0;
            } else {
                // 예외적 점프(혹시 있을 경우): 보수적으로 새 슬라이드만 0으로, 떠난 슬라이드는 유지
                // 필요시 여기에서 추가 정책 적용 가능
            }
            updated[realIndex] = 0;
            return updated;
        });
        setActiveIndex(realIndex);
        if (loop) {
            setCanPrev(true);
            setCanNext(true);
        } else {
            setCanPrev(realIndex > 0);
            setCanNext(realIndex < n - 1);
        }
    };
    const autoplayConfig = autoplay
        ? {
            delay: autoplayDelay,
            disableOnInteraction: false,
        }
        : false;
    return (

        <SwiperWrap className={`${className}`}>
            <Swiper
                modules={[
                    Autoplay,
                    ...(effect === "fade" ? [EffectFade] : []),
                    ...(navigation ? [Navigation] : []),
                ]}
                effect={effect}
                loop={loop}
                observer={true}
                observeParents={true}
                speed={speed}
                autoplay={autoplayConfig}
                navigation={navigation}
                onSlideChange={handleSlideChange}
                direction={direction}
                slidesPerView={slidesPerView}
                spaceBetween={spaceBetween}
                slidesPerGroup={slidesPerGroup}
                onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                    if (loop) {
                        setCanPrev(true);
                        setCanNext(true);
                    } else {
                        const idx = swiper.realIndex || 0;
                        setCanPrev(idx > 0);
                        setCanNext((slides?.length || 0) > 1 && idx < (slides.length - 1));
                    }
                }}
                nested={nested}
                touchReleaseOnEdges={touchReleaseOnEdges}
            >
                {slides.map((slide, idx) => (
                    <SwiperSlide
                        key={idx}
                        onClick={() => {
                            if(slideOnclick){
                                slideOnclick(idx);
                            }
                            if(slide.link && slide.link !== "undefined"){
                                router.push(slide.link);
                            }
                        }}
                        className={`${currentSlideIdx === idx ? "active" : ""} ${slide.link && slide.link !== "undefined" ? "point" : ""}`}
                    >
                        {
                            slide.imageItem &&
                            <div className={"image-wrap"}>
                                <img src={slide.imageItem} alt={`slide-${idx}`}/>
                            </div>
                        }
                        <div className={"sub-cont"}>
                            {
                                slide.title &&
                                <div className={"title-wrap"}>
                                    <p>{slide.title}</p>
                                </div>
                            }
                            {
                                slide.count &&
                                <div className={"count-wrap"}>
                                    <p>({slide.count})</p>
                                </div>
                            }
                            {
                                slide.desc &&
                                <div className={"desc-wrap"}>
                                    <p>{slide.desc}</p>
                                </div>
                            }
                            {
                                slide.content &&
                                <div className={"content-wrap"}>
                                    {slide.content()}
                                </div>
                            }
                        </div>
                    </SwiperSlide>
                ))}
            </Swiper>
            {slideButton && slides.length > 0 && (
                <div className="slide-buttons">
                    <button
                        type="button"
                        className={`nav-btn prev ${!canPrev ? "disabled" : ""}`}
                        onClick={() => canPrev && swiperRef.current?.slidePrev()}
                        disabled={!canPrev}
                        aria-label="이전 슬라이드"
                        title="이전"
                    >
                        ‹
                    </button>
                    <button
                        type="button"
                        className={`nav-btn next ${!canNext ? "disabled" : ""}`}
                        onClick={() => canNext && swiperRef.current?.slideNext()}
                        disabled={!canNext}
                        aria-label="다음 슬라이드"
                        title="다음"
                    >
                        ›
                    </button>
                </div>
            )}
            <div className="controller">
                {progressBar && (
                    <div className="progress-bar">
                        {slides.map((_, idx) => (
                            <div key={idx} className="bar">
                                <div
                                    className="fill"
                                    style={{ width: `${progressList[idx]}%` }}
                                />
                            </div>
                        ))}
                    </div>
                )}

                {pause && (
                    <div
                        className="pause-btn"
                        onClick={() => {
                            if (isPlaying) {
                                swiperRef.current?.autoplay.stop();
                            } else {
                                swiperRef.current?.autoplay.start();
                            }
                            setIsPlaying(!isPlaying);
                        }}
                        title={isPlaying ? "일시정지" : "재생"}
                    >
                        {isPlaying ? (
                            <HiMiniPause size={24} color="rgba(255,255,255,0.5)" />
                        ) : (
                            <FaPlay size={20} color="#fff" />
                        )}
                    </div>
                )}
            </div>
        </SwiperWrap>
    );
};

export default CustomSwiper;

// 스타일
const SwiperWrap = styled.div`
    position: relative;
    
    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .controller {
        display: flex;
        gap: 10px;
        align-items: center;
        justify-content: center;

        position: absolute;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
    }

    .progress-bar {
        display: flex;
        align-items: center;
        gap: 10px;

        .bar {
            width: 80px;
            height: 2px;
            background: rgba(255, 255, 255, 0.2);
            overflow: hidden;

            .fill {
                height: 100%;
                background: #fff;
                transition: width 0.05s linear;
            }
        }
    }

    .pause-btn {
        cursor: pointer;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;
